import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from "antd";
import { useMemo, useState } from "react";

import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import { useProctorSite } from "../../api/proctor";
import type { ProctorCandidate } from "../../api/types";
import { useAuthenticated } from "../../auth/SessionProvider";

/** Read-only site monitor: who has logged in, who is submitting, per room. */
export function ProctorPage() {
  const { principal } = useAuthenticated();
  const { catalog, groupName, regionName } = useNames();
  const fixedSite = principal.scope.sites !== "all" && principal.scope.sites.length === 1 ? principal.scope.sites[0] : undefined;
  const [chosenSite, setChosenSite] = useState<number>();
  const siteId = fixedSite ?? chosenSite;
  const status = useProctorSite(siteId, siteId !== undefined || fixedSite !== undefined);
  const [roomId, setRoomId] = useState<number>();
  const [keyword, setKeyword] = useState("");
  const data = status.data;
  const roomName = (id: number | null) => data?.rooms.find((room) => room.id === id)?.name ?? "—";

  const rows = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    return (data?.candidates ?? [])
      .filter((item) => roomId === undefined || item.roomId === roomId)
      .filter((item) => !text || item.username.toLowerCase().includes(text) || item.name.toLowerCase().includes(text));
  }, [data, roomId, keyword]);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space wrap>
        {fixedSite === undefined && (
          <Select<number>
            showSearch
            optionFilterProp="label"
            placeholder="选择考点"
            style={{ width: 320 }}
            value={chosenSite}
            onChange={(value) => { setChosenSite(value); setRoomId(undefined); }}
            options={(catalog.data?.sites ?? []).map((site) => ({ value: site.id, label: `${regionName(site.regionId)} / ${site.name}（${site.id}）` }))}
          />
        )}
        {data && (
          <Typography.Title level={4} style={{ margin: 0 }}>
            {data.site.regionName ? `${data.site.regionName} / ` : ""}{data.site.name}
          </Typography.Title>
        )}
        <Select<number>
          allowClear
          placeholder="全部考场"
          style={{ width: 200 }}
          value={roomId}
          onChange={setRoomId}
          disabled={!data}
          options={(data?.rooms ?? []).map((room) => ({ value: room.id, label: room.name }))}
        />
        <Button icon={<ReloadOutlined />} loading={status.isFetching} onClick={() => void status.refetch()}>刷新</Button>
        <Typography.Text type="secondary">统计时间 {formatTime(data?.generatedAt)}，每 15 秒自动刷新</Typography.Text>
      </Space>
      {status.error && <Alert type="error" showIcon message={status.error.message} />}
      {siteId === undefined && fixedSite === undefined && <Alert type="info" showIcon message="请选择一个考点。" />}
      {data && (
        <>
          <Row gutter={[16, 16]}>
            {[
              ["考生数", data.summary.total],
              ["已登录", data.summary.loggedIn],
              ["已提交", data.summary.submitted],
              ["已关闭账号", data.summary.disabled]
            ].map(([title, value]) => (
              <Col key={title} xs={12} md={6}>
                <Card><Statistic title={title} value={value} /></Card>
              </Col>
            ))}
          </Row>
          <Table<ProctorCandidate>
            rowKey="username"
            size="middle"
            dataSource={rows}
            pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (total) => `共 ${total} 人` }}
            title={() => (
              <input
                className="ant-input ant-input-outlined"
                style={{ width: 240 }}
                placeholder="准考证号或姓名"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
            )}
            columns={[
              { title: "考场", dataIndex: "roomId", render: roomName, sorter: (a, b) => (a.roomId ?? 0) - (b.roomId ?? 0) },
              { title: "座位", dataIndex: "seat", render: (value: string | null) => value ?? "—", sorter: (a, b) => (a.seat ?? "").localeCompare(b.seat ?? "") },
              { title: "准考证号", dataIndex: "username", sorter: (a, b) => a.username.localeCompare(b.username) },
              { title: "姓名", dataIndex: "name" },
              { title: "组别", dataIndex: "group", render: groupName },
              { title: "登录", render: (_, row) => row.lastLoginAt ? <Tag color="green">已登录</Tag> : <Tag>未登录</Tag> },
              { title: "最近登录", render: (_, row) => <><div>{formatTime(row.lastLoginAt)}</div><Typography.Text type="secondary">{row.lastLoginIp ?? ""}</Typography.Text></> },
              { title: "已作答", dataIndex: "answered", align: "right", sorter: (a, b) => a.answered - b.answered },
              { title: "最近提交", render: (_, row) => <><div>{formatTime(row.lastSubmitAt)}</div><Typography.Text type="secondary">{row.lastSubmitIp ?? ""}</Typography.Text></> },
              { title: "账号", render: (_, row) => row.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag> }
            ]}
          />
        </>
      )}
    </Space>
  );
}
